import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Apollo, QueryRef, gql } from 'apollo-angular';
import {
	CreateUserDocument,
	DeleteUserDocument,
	GetUserDocument,
	GetUsersDocument,
	SaveUserViewStateDocument,
	SendPasswordResetEmailDocument,
	TenantDocument,
	SendTwoFaEmailDocument,
	VerifyTwoFaCodeDocument,
	ForgetTrustedDeviceDocument,
} from '../../graphql/generated/gen-types';
@Injectable({
	providedIn: 'root',
})
export class UserService {
	userProfile: BehaviorSubject<any>;
	userProfileChanged: Observable<any>;

	constructor(private apollo: Apollo) {
		this.userProfile = new BehaviorSubject({});
		this.userProfileChanged = this.userProfile.asObservable();
	}

	public get getUserProfile() {
		return this.userProfile.value;
	}

	public nextUserProfile(user) {
		return this.userProfile.next(user);
	}

	public createUser(GcpipUserInput, tenantId: string): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: CreateUserDocument,
				variables: {
					GcpipUserInput,
					tenantId,
				},
				update: (cache, { data }) => {
					const existingUsers = cache.readQuery<any>({ query: GetUsersDocument, variables: { tenantId } });
					const newUser = data.createUser;
					cache.writeQuery({ query: GetUsersDocument, variables: { tenantId }, data: { getUsers: [...existingUsers.getUsers, newUser] } });
				},
			})
			.pipe(map((result: any) => result.data));
	}

	public deleteUser(uid: string, tenantId: string): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: DeleteUserDocument,
				variables: {
					uid: uid,
					tenantId: tenantId,
				},
				update: (cache, { data }) => {
					const existingUsers = cache.readQuery<any>({ query: GetUsersDocument, variables: { tenantId } });
					const deletedUserUid = data.deleteUser;
					const remainingUsers = existingUsers.getUsers.filter((user: any) => user.uid !== deletedUserUid);
					cache.writeQuery({ query: GetUsersDocument, variables: { tenantId }, data: { getUsers: remainingUsers } });
				},
			})
			.pipe(map((result: any) => result.data));
	}

	public getUsers(tenantId) {
		return this.apollo.watchQuery({
			query: GetUsersDocument,
			variables: {
				tenantId: tenantId,
			},
		});
	}

	public getUser(): QueryRef<any> {
		return this.apollo.watchQuery({
			query: GetUserDocument,
		});
	}

	public tenant(): QueryRef<any> {
		return this.apollo.watchQuery({
			query: TenantDocument,
		});
	}

	public sendPasswordResetEmail(email: string, tenantId: string): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: SendPasswordResetEmailDocument,
				variables: {
					email: email,
					tenantId: tenantId,
				},
			})
			.pipe(map((result: any) => result.data));
	}

	// 026: single source for the dashboard-layout rule — a stored preference wins (the backend
	// stamps 'matrix' for brand-new users via fixViewState); everyone else stays on the classic
	// dashboard until they switch (dual-layout period ends 2027-03-31).
	public dashboardLayoutOf(userProfile: any): 'classic' | 'matrix' {
		const stored = userProfile?.viewState?.dashboard?.layout;
		if (stored === 'classic' || stored === 'matrix') return stored;
		return 'classic';
	}

	// Feature 231: operator-triggered passwordless sign-in link for a user.
	// Inline gql (no generated Document yet — regenerate types at codegen time).
	// Enumeration-safe/throttled server-side; result is always neutral.
	public sendLoginLink(email: string, tenantId: string): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: gql`
					mutation sendLoginLink($tenantId: ID!, $email: String!) {
						sendLoginLink(tenantId: $tenantId, email: $email) {
							accepted
							throttled
						}
					}
				`,
				variables: {
					email: email,
					tenantId: tenantId,
				},
			})
			.pipe(map((result: any) => result.data));
	}

	public saveUserViewState(viewState): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: SaveUserViewStateDocument,
				variables: {
					viewState,
				},
				refetchQueries: [
					{
						query: GetUserDocument,
					},
				],
			})
			.pipe(map((result: any) => result.data));
	}

	public sendTwoFAEmail(deviceId: string): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: SendTwoFaEmailDocument,
				variables: { deviceId },
			})
			.pipe(map((result: any) => result.data));
	}

	public verifyTwoFACode(code: string, deviceId: string): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: VerifyTwoFaCodeDocument,
				variables: { code, deviceId },
			})
			.pipe(map((result: any) => result.data));
	}

	public forgetTrustedDevice(deviceId: string): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: ForgetTrustedDeviceDocument,
				variables: { deviceId },
			})
			.pipe(map((result: any) => result.data));
	}
}
